'use client'

import { useEffect } from 'react'
import { createGlobalStyle, keyframes } from 'styled-components'

import { fromMd, timingSlower } from '@syconium/little-miss-figgy'

import { miniCartWidthDesktop, miniCartWidthMobile } from '../../../../../../brunswick/constants'

// These are classes in Affirm's dialog. They are subject to change without notice.
const AFFIRM_CONTAINER = 'affirm-sandbox-container'
const DRAWER_CLASS = 'affirm-minicart-drawer'
const AFFIRM_IFRAME = 'affirm-sandbox-iframe'

const slideIn = keyframes`
  from { transform: translate3d(100%, 0, 0); }
  to { transform: translate3d(0, 0, 0); }
`
const slideInRtl = keyframes`
  from { transform: translate3d(-100%, 0, 0); }
  to { transform: translate3d(0, 0, 0); }
`

// Affirm injects the overlay at body level with position/inset/z-index set INLINE,
// so overrides must be !important. We only restyle the same-origin wrapper — the
// cross-origin iframe inside is 100%×100% and simply follows the new size.
const AffirmDrawerGlobalStyle = createGlobalStyle`
  .${AFFIRM_CONTAINER}.${DRAWER_CLASS} {
    top: 0 !important;
    bottom: 0 !important;
    inset-inline-end: 0 !important;
    inset-inline-start: auto !important; /* undo Affirm's inline inset:0 */
    width: ${miniCartWidthMobile} !important;
    z-index: ${o =>
      o.theme.zIndex.modals} !important; /* == minicart; injected later -> paints above it */
    /* slide-in mirroring the minicart Container; 'both' keeps the final (open) state */
    animation: ${slideIn} ${timingSlower} ease both;

    ${fromMd} {
      width: ${miniCartWidthDesktop} !important;
    }
  }

  .rtl .${AFFIRM_CONTAINER}.${DRAWER_CLASS} {
    animation-name: ${slideInRtl};
  }

  .${AFFIRM_IFRAME} {
    /* Affirm honors device theme preference. FIGS doesn't, so we force light mode. */
    color-scheme: light !important;
  }

  @media (prefers-reduced-motion: reduce) {
    .${AFFIRM_CONTAINER}.${DRAWER_CLASS} {
      animation: none !important; /* !important clears the .rtl animation-name too */
    }
  }
`

const useAffirmModalDrawer = () => {
  useEffect(() => {
    // `magnolia` has no `body-scroll-lock` of its own; freeze `<body>` while the Affirm overlay is open.
    let previousOverflow: string | null = null
    const lockScroll = () => {
      if (previousOverflow !== null) return // already locked
      previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    const unlockScroll = () => {
      if (previousOverflow === null) return // not locked
      document.body.style.overflow = previousOverflow
      previousOverflow = null
    }

    /**
     * Opens the drawer by tagging the element with `DRAWER_CLASS`
     */
    const open = (el: HTMLElement) => {
      el.classList.add(DRAWER_CLASS) // triggers the slide-in animation
      lockScroll()
    }
    // already present (opened before mount)?
    const existing = document.querySelector<HTMLElement>(`.${AFFIRM_CONTAINER}`)
    if (existing) open(existing)

    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node instanceof HTMLElement && node.classList.contains(AFFIRM_CONTAINER)) open(node)
        })
        m.removedNodes.forEach(node => {
          if (node instanceof HTMLElement && node.classList.contains(AFFIRM_CONTAINER))
            unlockScroll()
        })
      }
    })
    observer.observe(document.body, { childList: true })

    return () => {
      observer.disconnect()
      unlockScroll()
      // The modal is appended to <body>, so it outlives this component. When the minicart
      // closes, MiniCartContent (and this controller + its drawer styles) unmounts. We tear the
      // modal down here, otherwise it orphans and reverts to Affirm's full-screen styling.
      document.querySelector(`.${AFFIRM_CONTAINER}`)?.remove()
    }
  }, [])
}

export const AffirmModalDrawer = () => {
  useAffirmModalDrawer()
  return <AffirmDrawerGlobalStyle />
}
